import React, {useCallback, useMemo} from 'react'
import {decode} from 'entities'
import removeMarkdownAndHtml from 'remove-markdown-and-html'
import sanitizeImageUrl from 'utils/MiscUtils/sanitizeImageUrl'
import classnames from 'classnames'
import AgentBadge from 'components/people/AgentBadge'

// Shared helpers for the react-select style components.
// It includes formatting options with images and tooltips and custom placeholders
// It will need some variables from useRailsSelect to work properly.
const useOptionFormatting = (
  options,
  {
    iconOnlySelection,
    showSubtitle = true,
    placeholderData,
  },
) => {
  const hasImageDataInOptions = useMemo(
    () => options.some((option) => option.imageData),
    [options],
  )

  const optionImage = useCallback((imageData) => {
    if (!imageData)
      return null

    const classes = classnames(
      'rails_select--option-img',
      imageData.css_classes,
    )

    if (imageData.fa_icon) {
      return React.createElement(
        'div',
        {className: classes},
        React.createElement('i', {className: `fa ${imageData.fa_icon} icon-element`}),
      )
    }

    // We're using the background image approach to let css handle image loading
    // and sizing, etc for performance and simplicity.
    const sanitizedUrl = sanitizeImageUrl(imageData.image)
    if (!sanitizedUrl)
      return null
    return React.createElement(
      'div',
      {
        className: classes,
        style: imageData.image ? {backgroundImage: `url(${sanitizedUrl})`} : undefined,
      },
    )
  }, [])

  const convertToPlainText = (str) => {
    if (!str)
      return ''

    // Decode HTML entities and remove markdown/HTML
    return decode(
      removeMarkdownAndHtml(str)
        .replace(/[\n\r]+/g, ' ')
        .replace(/~/g, ''),
    )
  }

  const agentBadge = useCallback(() => React.createElement(
    AgentBadge,
    {className: 'agent-badge__icon', title: 'Agent'},
  ), [])

  const optionTitleAndSubtitle = useCallback((option) => React.createElement(
    'span',
    {className: 'rails_select--option-title-container'},
    React.createElement('span', {className: 'rails_select--option-title'}, convertToPlainText(option.label)),
    option.isAgent && agentBadge(),
    option.subtitle && showSubtitle
    && React.createElement('span', {className: 'rails_select--option-subtitle'}, convertToPlainText(option.subtitle)),
  ), [showSubtitle, agentBadge])

  const formatOptionLabel = useCallback((option) => React.createElement(
    'span',
    {className: 'rails_select--option'},
    hasImageDataInOptions && optionImage(option.imageData),
    optionTitleAndSubtitle(option),
  ), [optionImage, hasImageDataInOptions, optionTitleAndSubtitle])

  const iconOnlySingleValue = useCallback(({data}) => React.createElement(
    'div',
    {className: 'rails_select__icon-only-single-value'},
    React.createElement(
      'span',
      {
        className: 'rails_select--option',
        title: convertToPlainText(data.label),
        'data-original-title': convertToPlainText(data.label), // keep Bootstrap's cache in sync
      },
      optionImage(data.imageData),
    ),
  ), [optionImage])

  const customPlaceholder = useCallback(({selectProps}) => {
    const {placeholder} = selectProps

    return React.createElement(
      'div',
      {
        className: `rails_select__custom-placeholder ${
          iconOnlySelection ? 'icon-only-single-value' : ''
        }`,
      },
      React.createElement(
        'div',
        {className: 'rails_select--option'},
        optionImage(placeholder.imageData),
        !iconOnlySelection
        && React.createElement(
          'span',
          {className: 'rails_select--option-title'},
          placeholder.label,
        ),
      ),
    )
  }, [optionImage, iconOnlySelection])

  const components = useMemo(() => {
    const result = {}
    if (iconOnlySelection)
      result.SingleValue = iconOnlySingleValue
    if (placeholderData)
      result.Placeholder = customPlaceholder
    return result
  }, [iconOnlySelection, placeholderData, iconOnlySingleValue, customPlaceholder])

  return {hasImageDataInOptions, formatOptionLabel, components}
}

export default useOptionFormatting
